import React from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, Eye, BarChart3, Zap } from 'lucide-react';
import PageTitle from '../layout/PageTitle';
import OpenChart from '../ui/OpenChart';
import BlogSEO from '../ui/BlogSEO';

const Blog: React.FC = () => {
  return (
    <div className="content">
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 w-full">
      <BlogSEO />
      <PageTitle title="Blog - SentiHype" />
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="space-y-8"
        >
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              SentiHype Blog
            </h1>
            <p className="text-xl text-[#bbb] max-w-2xl mx-auto">
              Real-time insights into social sentiment and market movements
            </p>
          </div>

          {/* Article */}
          <article className="bg-[#1a1a1a] border border-[#333] rounded-xl p-8 shadow-lg" itemScope itemType="https://schema.org/Article">
            {/* Article Header */}
            <div className="mb-8">
              <div className="flex items-center space-x-2 mb-4">
                <span className="px-3 py-1 bg-[#4ff6e6]/10 text-[#4ff6e6] text-sm rounded-full border border-[#4ff6e6]/20" itemProp="articleSection">
                  Case Study
                </span>
                <time className="text-[#bbb] text-sm" itemProp="datePublished" dateTime="2025-07-28">July 2025</time>
              </div>
              
              <h1 className="text-3xl md:text-4xl font-bold text-white mb-4 leading-tight" itemProp="headline">
                How SentiHype Detected Opendoor's Meme Stock Surge Before It Exploded
              </h1>
              
              <p className="text-lg text-[#ccc] leading-relaxed" itemProp="description">
                In July 2025, Opendoor (ticker: $OPEN) became one of the most dramatic meme-stock stories of the year. 
                The stock surged over 370% in just a few days, largely fueled by social media hype and retail momentum on Reddit and X (formerly Twitter).
              </p>
            </div>

            {/* Key Insight */}
            <div className="bg-[#333] border-l-4 border-[#4ff6e6] p-6 rounded-r-lg mb-8">
              <div className="flex items-start space-x-3">
                <Eye className="w-6 h-6 text-[#4ff6e6] mt-1 flex-shrink-0" />
                <div>
                  <h3 className="text-xl font-semibold text-white mb-2">The Key Insight</h3>
                  <p className="text-[#ccc] leading-relaxed">
                    <strong>SentiHype detected the spike in Reddit activity around $OPEN before the price went vertical.</strong>
                  </p>
                </div>
              </div>
            </div>

            {/* Real-Time Signal Section */}
            <section className="mb-8" itemProp="articleBody">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-10 h-10 bg-[#4ff6e6]/10 rounded-lg flex items-center justify-center">
                  <Zap className="w-6 h-6 text-[#4ff6e6]" />
                </div>
                <h3 className="text-2xl font-semibold text-white">🔍 Real-Time Reddit Signal Before the Pump</h3>
              </div>
              
              <p className="text-[#ccc] mb-4 leading-relaxed">
                SentiHype continuously monitors Reddit for:
              </p>
              
              <ul className="space-y-3 mb-6">
                <li className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-[#4ff6e6] rounded-full"></div>
                  <span className="text-[#ccc]">Ticker mentions (e.g., $OPEN)</span>
                </li>
                <li className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-[#4ff6e6] rounded-full"></div>
                  <span className="text-[#ccc]">Sentiment trends</span>
                </li>
                <li className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-[#4ff6e6] rounded-full"></div>
                  <span className="text-[#ccc]">Velocity of mentions across active retail communities</span>
                </li>
              </ul>
              
              <div className="bg-[#333] p-6 rounded-lg">
                <p className="text-[#ccc] leading-relaxed">
                  On July 21st, our system recorded a sudden spike in $OPEN mentions—from 1–2 per hour to nearly 9 in a single interval, 
                  and sustained elevated activity through July 22nd.
                </p>
                <p className="text-[#ccc] leading-relaxed mt-3">
                  At the same time, bullish sentiment started rising. Then came the breakout.
                </p>
                <p className="text-[#ccc] leading-relaxed mt-3">
                  By the next day, Opendoor stock had surged to over $9, up hundreds of percent from earlier in the month. 
                  The volume skyrocketed. The squeeze was on.
                </p>
              </div>
            </section>

            {/* Chart Section */}
            <section className="mb-8">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-10 h-10 bg-[#D4AF37]/10 rounded-lg flex items-center justify-center">
                  <BarChart3 className="w-6 h-6 text-[#D4AF37]" />
                </div>
                <h3 className="text-2xl font-semibold text-white">🔢 Mentions vs. Price: The Proof</h3>
              </div>
              
              <p className="text-[#ccc] mb-6 leading-relaxed">
                Below is the real SentiHype chart of $OPEN Reddit mentions over time, plotted against approximate stock price action for the same period:
              </p>
              
              {/* Real Chart */}
              <div className="mb-6">
                <OpenChart />
              </div>
              
              <div className="bg-[#333] p-6 rounded-lg">
                <p className="text-[#ccc] font-semibold mb-3">As you can see:</p>
                <ul className="space-y-2">
                  <li className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-[#4ff6e6] rounded-full"></div>
                    <span className="text-[#ccc]">Mentions surged before price.</span>
                  </li>
                  <li className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-[#4ff6e6] rounded-full"></div>
                    <span className="text-[#ccc]">There was a real, observable lead-in signal.</span>
                  </li>
                  <li className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-[#4ff6e6] rounded-full"></div>
                    <span className="text-[#ccc]">SentiHype caught it.</span>
                  </li>
                </ul>
              </div>
            </section>

            {/* Why It Matters Section */}
            <section className="mb-8">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-10 h-10 bg-[#ff6b6b]/10 rounded-lg flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-[#ff6b6b]" />
                </div>
                <h3 className="text-2xl font-semibold text-white">🚀 Why It Matters</h3>
              </div>
              
              <p className="text-[#ccc] mb-4 leading-relaxed">
                This wasn't luck. This is what SentiHype is designed to do:
              </p>
              
              <ul className="space-y-3 mb-6">
                <li className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-[#4ff6e6] rounded-full"></div>
                  <span className="text-[#ccc]">Scan for sudden Reddit interest in tickers</span>
                </li>
                <li className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-[#4ff6e6] rounded-full"></div>
                  <span className="text-[#ccc]">Detect shifts in attention before the market reacts</span>
                </li>
                <li className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-[#4ff6e6] rounded-full"></div>
                  <span className="text-[#ccc]">Help traders see what the crowd is about to act on</span>
                </li>
              </ul>
              
              <div className="bg-[#333] border-l-4 border-[#ff6b6b] p-6 rounded-r-lg">
                <p className="text-[#ccc] leading-relaxed">
                  <strong>You don't need to chase. You just need to listen to the signal before the noise.</strong>
                </p>
              </div>
            </section>


          </article>
        </motion.div>
    </div>
    </div>
  );
};

export default Blog; 